import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { oneKlicks } from '../../../SDK/oneclick';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const STATUS_OPTIONS = [
    { label: 'Active', value: 'active' },
    { label: 'Pending Renewal', value: 'pending_renewal' },
    { label: 'Expired', value: 'expired' },
];

const PAYMENT_FREQUENCIES = [
    { label: 'Monthly', value: 'monthly' },
    { label: 'Quarterly', value: 'quarterly' },
    { label: 'Semi Annual', value: 'semi_annual' },
    { label: 'Annual', value: 'annual' },
    { label: 'One-time', value: 'one_time' },
];

const NewPolicy = () => {
    const { userData } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [coverageSuggestions, setCoverageSuggestions] = useState([]);
    const [products, setProducts] = useState([]);
    const [profileInfo, setProfileInfo] = useState({ companyId: null, agentId: null });
    const [productInfo, setProductInfo] = useState({ productId: null, list: [] });
    const [profileReady, setProfileReady] = useState(false);
    const [profileLoading, setProfileLoading] = useState(false);

    const [formData, setFormData] = useState({
        policy_number: '',
        customer_id: '',
        policy_holder_name: '',
        coverage_type: '',
        coverage_amount: '',
        premium_amount: '',
        payment_frequency: 'monthly',
        product_id: '',
        nominee_name: '',
        nominee_relationship: '',
        start_date: '',
        end_date: '',
        status: 'active',
        notes: '',
    });

    useEffect(() => {
        if (!formData.policy_number) {
            setFormData((prev) => ({ ...prev, policy_number: generatePolicyNumber() }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        initializeProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.id]);

    useEffect(() => {
        if (!profileReady) return;
        fetchReferenceData(profileInfo.companyId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileReady, profileInfo.companyId]);

    useEffect(() => {
        if (!formData.customer_id) return;
        const selectedCustomer = customers.find((cust) => String(cust.id) === String(formData.customer_id));
        if (selectedCustomer && !formData.policy_holder_name) {
            setFormData((prev) => ({ ...prev, policy_holder_name: selectedCustomer.name || '' }));
        }
    }, [formData.customer_id, customers]);

    const generatePolicyNumber = () => {
        const year = new Date().getFullYear();
        const randomNum = Math.floor(Math.random() * 100000).toString().padStart(5, '0');
        return `POL-${year}-${randomNum}`;
    };

    const initializeProfile = async () => {
        if (!userData?.id) {
            setProfileInfo({ companyId: null, agentId: null });
            setProductInfo({ productId: null, list: [] });
            setProfileReady(true);
            return;
        }

        setProfileLoading(true);
        setProfileReady(false);

        try {
            let companyId = userData?.company_id || userData?.companyId || userData?.company || null;
            let agentId = userData?.agent_id || userData?.agentId || null;
            let productId = null;
            const timestamp = new Date().toISOString().slice(0, 19).replace('T', ' ');

            if (!companyId) {
                const companyRes = await oneKlicks.read('', 'insurance_companies', {}, 'id ASC', '', '', '0,1');
                if (companyRes?.success && Array.isArray(companyRes?.data) && companyRes.data.length > 0) {
                    companyId = companyRes.data[0].id;
                } else {
                    const createCompanyRes = await oneKlicks.create('', 'insurance_companies', {
                        company_uid: `INS-${Date.now()}`,
                        name: 'Default Insurance Co',
                        legal_name: 'Default Insurance Company Pvt Ltd',
                        email: userData?.email || null,
                        phone: userData?.phone || null,
                        created_at: timestamp,
                        updated_at: timestamp,
                    });
                    if (createCompanyRes?.success) {
                        companyId = createCompanyRes.insertId || createCompanyRes?.data?.id || null;
                    }
                }
            }

            if (companyId && !agentId) {
                const agentRes = await oneKlicks.read('', 'insurance_agents', { company_id: companyId }, 'id ASC', '', '', '0,1');
                if (agentRes?.success && Array.isArray(agentRes?.data) && agentRes.data.length > 0) {
                    agentId = agentRes.data[0].id;
                } else {
                    const createAgentRes = await oneKlicks.create('', 'insurance_agents', {
                        agent_uid: `AGT-${Date.now()}`,
                        company_id: companyId,
                        name: userData?.display_name || userData?.name || 'Primary Agent',
                        email: userData?.email || null,
                        phone: userData?.phone || null,
                        status: 'active',
                        created_at: timestamp,
                        updated_at: timestamp,
                    });
                    if (createAgentRes?.success) {
                        agentId = createAgentRes.insertId || createAgentRes?.data?.id || null;
                    }
                }
            }

            if (companyId) {
                const productRes = await oneKlicks.read('', 'insurance_products', { company_id: companyId }, 'id ASC', '', '', '0,5');
                if (productRes?.success && Array.isArray(productRes?.data) && productRes.data.length > 0) {
                    productId = productRes.data[0].id;
                    setProductInfo({ productId, list: productRes.data });
                } else {
                    const defaultProducts = [
                        {
                            product_uid: `PRD-${Date.now()}1`,
                            company_id: companyId,
                            name: 'Health Insurance - Standard',
                            description: 'Standard health cover',
                            created_at: timestamp,
                            updated_at: timestamp,
                        },
                        {
                            product_uid: `PRD-${Date.now()}2`,
                            company_id: companyId,
                            name: 'Family Floater Plan',
                            description: 'Family floater coverage',
                            created_at: timestamp,
                            updated_at: timestamp,
                        },
                    ];

                    const createdProducts = [];
                    for (const product of defaultProducts) {
                        const createProductRes = await oneKlicks.create('', 'insurance_products', product);
                        if (createProductRes?.success) {
                            createdProducts.push({ id: createProductRes.insertId || createProductRes?.data?.id, ...product });
                        }
                    }

                    if (createdProducts.length > 0) {
                        productId = createdProducts[0].id;
                        setProductInfo({ productId, list: createdProducts });
                    } else {
                        setProductInfo({ productId: null, list: [] });
                    }
                }
            }

            if (companyId && agentId) {
                if (userData?.company_id !== companyId || userData?.agent_id !== agentId) {
                    try {
                        await oneKlicks.update('', 'users', {
                            company_id: companyId,
                            agent_id: agentId,
                            updated_at: timestamp,
                        }, { id: userData.id });
                    } catch (updateError) {
                        console.error('Error updating user profile with company/agent IDs:', updateError);
                    }
                }
            }

            setProfileInfo({ companyId: companyId || null, agentId: agentId || null });
            if (productId) {
                setFormData((prev) => ({ ...prev, product_id: String(productId) }));
            }
        } catch (error) {
            console.error('Error ensuring insurance profile:', error);
            toast.error('Unable to initialize insurance profile. Please try again or contact support.');
            setProfileInfo({ companyId: null, agentId: null });
            setProductInfo({ productId: null, list: [] });
        } finally {
            setProfileReady(true);
            setProfileLoading(false);
        }
    };

    const fetchReferenceData = async (effectiveCompanyId) => {
        setLoading(true);
        try {
            const baseConditions = effectiveCompanyId ? { company_id: effectiveCompanyId } : {};

            const customerRes = await oneKlicks.read(
                '',
                'insurance_customers',
                baseConditions,
                'id DESC',
                '',
                '',
                '0,200'
            );
            console.log('Insurance customers read response:', customerRes);

            let customerRows = [];
            if (customerRes?.success) {
                customerRows = Array.isArray(customerRes?.data)
                    ? customerRes.data
                    : Array.isArray(customerRes?.data?.items)
                        ? customerRes.data.items
                        : [];

                if (customerRows.length === 0 && Object.keys(baseConditions).length > 0) {
                    console.log('No customers found with company filter. Trying unfiltered fetch.');
                    const fallbackRes = await oneKlicks.read(
                        '',
                        'insurance_customers',
                        {},
                        'id DESC',
                        '',
                        '',
                        '0,200'
                    );
                    console.log('Insurance customers fallback response:', fallbackRes);
                    if (fallbackRes?.success) {
                        customerRows = Array.isArray(fallbackRes?.data)
                            ? fallbackRes.data
                            : Array.isArray(fallbackRes?.data?.items)
                                ? fallbackRes.data.items
                                : [];
                    }
                }

                setCustomers(customerRows);
            } else {
                console.warn('Failed to fetch insurance customers:', customerRes);
                toast.error('Unable to load customers list.');
                setCustomers([]);
            }

            const policyRes = await oneKlicks.read(
                '',
                'insurance_policies',
                baseConditions,
                'created_at DESC',
                '',
                '',
                '0,200'
            );
            if (policyRes?.success && Array.isArray(policyRes?.data)) {
                const uniques = Array.from(
                    new Set(
                        policyRes.data
                            .map((p) => p.coverage_type)
                            .filter((type) => typeof type === 'string' && type.trim().length > 0)
                    )
                );
                setCoverageSuggestions(uniques);
            }

            if (!productInfo.list.length && effectiveCompanyId) {
                const productRes = await oneKlicks.read(
                    '',
                    'insurance_products',
                    { company_id: effectiveCompanyId },
                    'id ASC',
                    '',
                    '',
                    '0,10'
                );
                if (productRes?.success && Array.isArray(productRes?.data) && productRes.data.length > 0) {
                    setProductInfo((prev) => ({
                        productId: prev.productId || productRes.data[0].id,
                        list: productRes.data,
                    }));
                    setFormData((prev) => ({ ...prev, product_id: prev.product_id || String(productRes.data[0].id) }));
                }
            }
        } catch (error) {
            console.error('Error loading reference data:', error);
            toast.error('Failed to load reference data');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSelectCustomer = (e) => {
        const { value } = e.target;
        const selectedCustomer = customers.find((cust) => String(cust.id) === value);
        setFormData((prev) => ({
            ...prev,
            customer_id: value,
            policy_holder_name: selectedCustomer?.name || selectedCustomer?.full_name || prev.policy_holder_name,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.policy_number || !formData.policy_holder_name || !formData.coverage_type || !formData.premium_amount || !formData.start_date) {
            toast.error('Please fill in all required fields');
            return;
        }

        if (!formData.customer_id) {
            toast.error('Please select a customer for this policy');
            return;
        }

        const { companyId, agentId } = profileInfo;
        const productId = formData.product_id ? Number(formData.product_id) : productInfo.productId;
        if (!companyId || !agentId || !productId) {
            toast.error('Cannot determine company, agent, or product information. Please reload and try again.');
            return;
        }

        setSubmitting(true);
        try {
            const now = new Date();
            const formattedTimestamp = now.toISOString().slice(0, 19).replace('T', ' ');

            const payload = {
                policy_number: formData.policy_number,
                policy_holder_name: formData.policy_holder_name,
                customer_id: Number(formData.customer_id),
                coverage_type: formData.coverage_type,
                coverage_amount: formData.coverage_amount ? parseFloat(formData.coverage_amount) : 0,
                premium_amount: parseFloat(formData.premium_amount),
                payment_frequency: formData.payment_frequency || 'monthly',
                product_id: productId,
                nominee_name: formData.nominee_name || null,
                nominee_relationship: formData.nominee_relationship || null,
                start_date: formData.start_date,
                end_date: formData.end_date || null,
                status: formData.status,
                notes: formData.notes || null,
                user_id: userData?.id || null,
                company_id: companyId,
                agent_id: agentId,
                created_at: formattedTimestamp,
                updated_at: formattedTimestamp,
            };

            console.log('Creating insurance policy with payload:', payload);

            const response = await oneKlicks.create('', 'insurance_policies', payload);
            console.log('Create policy response:', response);

            if (response?.success) {
                toast.success('Policy created successfully!');
                navigate('/insurance/policies');
            } else {
                toast.error(response?.message || response?.error || 'Failed to create policy');
            }
        } catch (error) {
            console.error('Error creating policy:', error);
            toast.error('Failed to create policy');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="subbody" style={{ width: '100%', padding: '24px' }}>
            <div className="dc-haslayout dc-dbsectionspace" style={{ maxWidth: '1080px', margin: '0 auto' }}>
                <div className="dc-dashboardbox">
                    <div
                        className="dc-dashboardboxtitle"
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            flexWrap: 'wrap',
                            gap: '12px',
                        }}
                    >
                        <div>
                            <h2>Create New Policy</h2>
                            <p style={{ color: '#6c757d', marginTop: '4px' }}>
                                Capture policy details and assign them to a customer.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate('/insurance/policies')}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="insurance-policy-form"
                                className="btn btn-primary"
                                disabled={submitting}
                            >
                                {submitting ? 'Saving...' : 'Create Policy'}
                            </button>
                        </div>
                    </div>

                    <div className="dc-dashboardboxcontent" style={{ padding: '24px' }}>
                        {profileLoading || !profileReady || loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <form id="insurance-policy-form" className="dc-formtheme" onSubmit={handleSubmit}>
                                <div className="row" style={{ rowGap: '24px' }}>
                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="policy_number">Policy Number</label>
                                            <input
                                                type="text"
                                                id="policy_number"
                                                name="policy_number"
                                                className="form-control"
                                                value={formData.policy_number}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="customer_id">Customer</label>
                                            <select
                                                id="customer_id"
                                                name="customer_id"
                                                className="form-control"
                                                value={formData.customer_id}
                                                onChange={handleSelectCustomer}
                                                required
                                            >
                                                <option value="">Select Customer</option>
                                                {customers.map((cust) => {
                                                    const label =
                                                        cust.name ||
                                                        cust.full_name ||
                                                        cust.customer_name ||
                                                        `Customer #${cust.id}`;
                                                    const contact = cust.email || cust.phone || '';
                                                    return (
                                                        <option key={cust.id} value={cust.id}>
                                                            {label}
                                                            {contact ? ` – ${contact}` : ''}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="policy_holder_name">Policy Holder Name</label>
                                            <input
                                                type="text"
                                                id="policy_holder_name"
                                                name="policy_holder_name"
                                                className="form-control"
                                                value={formData.policy_holder_name}
                                                onChange={handleChange}
                                                required
                                                placeholder="e.g. John Doe"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="coverage_type">Coverage Type</label>
                                            <input
                                                list="coverage-options"
                                                id="coverage_type"
                                                name="coverage_type"
                                                className="form-control"
                                                value={formData.coverage_type}
                                                onChange={handleChange}
                                                required
                                                placeholder="e.g. Health Insurance"
                                            />
                                            {coverageSuggestions.length > 0 && (
                                                <datalist id="coverage-options">
                                                    {coverageSuggestions.map((type) => (
                                                        <option key={type} value={type} />
                                                    ))}
                                                </datalist>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="coverage_amount">Coverage Amount (₹)</label>
                                            <input
                                                type="number"
                                                id="coverage_amount"
                                                name="coverage_amount"
                                                className="form-control"
                                                min="0"
                                                step="0.01"
                                                value={formData.coverage_amount}
                                                onChange={handleChange}
                                                placeholder="Optional"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="premium_amount">Premium Amount (₹)</label>
                                            <input
                                                type="number"
                                                id="premium_amount"
                                                name="premium_amount"
                                                className="form-control"
                                                min="0"
                                                step="0.01"
                                                value={formData.premium_amount}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="payment_frequency">Payment Frequency</label>
                                            <select
                                                id="payment_frequency"
                                                name="payment_frequency"
                                                className="form-control"
                                                value={formData.payment_frequency}
                                                onChange={handleChange}
                                            >
                                                {PAYMENT_FREQUENCIES.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="product_id">Product</label>
                                            <select
                                                id="product_id"
                                                name="product_id"
                                                className="form-control"
                                                value={formData.product_id}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select Product</option>
                                                {(productInfo.list.length ? productInfo.list : products).map((product) => (
                                                    <option key={product.id} value={product.id}>
                                                        {product.name || product.product_uid || `Product #${product.id}`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="nominee_name">Nominee Name (optional)</label>
                                            <input
                                                type="text"
                                                id="nominee_name"
                                                name="nominee_name"
                                                className="form-control"
                                                value={formData.nominee_name}
                                                onChange={handleChange}
                                                placeholder="Beneficiary name"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="nominee_relationship">Nominee Relationship</label>
                                            <input
                                                type="text"
                                                id="nominee_relationship"
                                                name="nominee_relationship"
                                                className="form-control"
                                                value={formData.nominee_relationship}
                                                onChange={handleChange}
                                                placeholder="e.g. Spouse, Child"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="status">Status</label>
                                            <select
                                                id="status"
                                                name="status"
                                                className="form-control"
                                                value={formData.status}
                                                onChange={handleChange}
                                                required
                                            >
                                                {STATUS_OPTIONS.map((status) => (
                                                    <option key={status.value} value={status.value}>
                                                        {status.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="start_date">Start Date</label>
                                            <input
                                                type="date"
                                                id="start_date"
                                                name="start_date"
                                                className="form-control"
                                                value={formData.start_date}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="end_date">End Date (optional)</label>
                                            <input
                                                type="date"
                                                id="end_date"
                                                name="end_date"
                                                className="form-control"
                                                value={formData.end_date}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="form-group">
                                            <label htmlFor="notes">Notes (optional)</label>
                                            <textarea
                                                id="notes"
                                                name="notes"
                                                className="form-control"
                                                rows="3"
                                                placeholder="Any additional information or terms"
                                                value={formData.notes}
                                                onChange={handleChange}
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewPolicy;
